import { isCancel as isAxiosCancelError } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { StatusBanner } from '@xingternal/banner';
import { trackEvent } from '@xingternal/hops-tracking';
import { IconCirclePlus, IconUpload } from '@xingternal/icons';
import { BodyCopy, Headline } from '@xingternal/typography';
import { Column, Row } from '@xingternal/xing-grid';

import {
  DEFAULT_ACCEPTED_MIME_TYPES,
  DEFAULT_MAX_FILESIZE_BYTES,
  UPLOAD_ERRORS,
  UploadError,
  trackFormError,
} from '@/domain/job-application/helpers';
import { UploadedFile } from '@/domain/job-application/hooks';

import { useShowAdditionalDocs } from '../../contexts';
import { useDocumentsUploadCopies } from './use-documents-upload-copies';

import * as S from './documents-upload.styles';

const ACTION_ORIGIN = 'jobs_upload_';

type Props = {
  buttonRef: (instance: unknown) => void;
  onUploadFile: (file: File) => Promise<void>;
  onDeleteUpload: (upload: UploadedFile) => Promise<void>;
  trackingOrigin: 'cv' | 'other';
  uploads: UploadedFile[];
  maxFiles: number;
  isReduced?: boolean;
  showUploadInProgressError?: boolean;
  resetShowUploadInProgressError?: () => void;
};

export const DocumentsUpload: React.FC<Props> = React.memo(
  ({
    buttonRef,
    onUploadFile,
    onDeleteUpload,
    showUploadInProgressError = false,
    resetShowUploadInProgressError,
    trackingOrigin,
    uploads,
    maxFiles,
    isReduced = false,
  }) => {
    const uploadFileRef = useRef<HTMLInputElement>(null);
    const [uploadError, setUploadError] = useState<UploadError>(null);
    const copyKeys = useDocumentsUploadCopies();
    const showAdditionalDocs = useShowAdditionalDocs();

    useEffect(() => {
      if (showUploadInProgressError) setUploadError('UPLOAD_IN_PROGRESS');
    }, [showUploadInProgressError]);

    const handleDeleteUpload = useCallback(
      async (upload): Promise<void> => {
        if (uploadFileRef.current) uploadFileRef.current.value = '';
        onDeleteUpload(upload).catch(() => setUploadError('DELETE'));
      },
      [onDeleteUpload]
    );

    const handleUploadFile = useCallback(
      (event): void => {
        trackEvent('PropTrackAction', {
          PropTrackAction: 'jobs_upload',
          PropActionOrigin: `${ACTION_ORIGIN}${trackingOrigin}`,
        });

        const files = [...event.target.files];

        if (uploads.length + files.length > maxFiles) {
          setUploadError(maxFiles === 1 ? 'SINGLE_FILE_ALLOWED' : 'MAX_FILES_ALLOWED');
          trackFormError('jobs_upload_error_max_file_number_exceeded', trackingOrigin);
        } else {
          files.forEach((file) => {
            if (file.size > DEFAULT_MAX_FILESIZE_BYTES) {
              trackFormError('jobs_upload_error_file_size_exceeded', trackingOrigin);
              setUploadError('FILE_SIZE_TOO_LARGE');
              return;
            } else if (!file.type || !DEFAULT_ACCEPTED_MIME_TYPES.includes(file.type)) {
              trackFormError('jobs_upload_error_invalid_file_type', trackingOrigin);
              setUploadError('MIME_TYPE_NOT_SUPPORTED');
              return;
            }

            onUploadFile(file)
              .then(() => setUploadError(null))
              .catch((error) => {
                if (!isAxiosCancelError(error)) {
                  setUploadError('GENERIC');
                  trackFormError('jobs_upload_error_network', trackingOrigin);
                }
              });
          });
        }

        event.target.value = '';
      },
      [maxFiles, onUploadFile, trackingOrigin, uploads.length]
    );

    const handleUploadButtonClick = useCallback((): void => {
      if (uploadFileRef.current) {
        uploadFileRef.current.click();
        trackEvent('PropTrackAction', {
          PropTrackAction: 'jobs_upload_open',
          PropActionOrigin: `${ACTION_ORIGIN}${trackingOrigin}`,
        });
      }
    }, [trackingOrigin]);

    const handleErrorBannerClose = useCallback((): void => {
      uploadError === 'UPLOAD_IN_PROGRESS' && resetShowUploadInProgressError?.();
      setUploadError(null);
    }, [resetShowUploadInProgressError, uploadError]);

    return (
      <>
        {isReduced ? (
          <S.ReducedUploadButton
            icon={IconCirclePlus}
            size="medium"
            data-testid="reduced-upload-button"
            disabled={uploads.length >= maxFiles}
            onClick={handleUploadButtonClick}
            innerRef={buttonRef}
          >
            <FormattedMessage id={copyKeys.uploadOtherDocumentsButton} />
          </S.ReducedUploadButton>
        ) : (
          <>
            <Headline size="large">
              <FormattedMessage id={copyKeys.headline} />
            </Headline>
            <Headline size="medium">
              <FormattedMessage id={copyKeys.body} />
            </Headline>
            <Row>
              <Column size={12} sizeTablet={7}>
                {showAdditionalDocs ? (
                  <BodyCopy size="small" noMargin>
                    <FormattedMessage id={copyKeys.filesHint} />
                  </BodyCopy>
                ) : null}
                <BodyCopy size="small" noMargin>
                  <FormattedMessage id={copyKeys.formatHint} />
                </BodyCopy>
              </Column>
              <Column size={12} sizeTablet={5}>
                <S.UploadButton
                  disabled={uploads.length >= maxFiles}
                  icon={IconUpload}
                  size="medium"
                  variant="secondary"
                  data-testid="upload-button"
                  onClick={handleUploadButtonClick}
                  innerRef={buttonRef}
                >
                  <FormattedMessage id={copyKeys.uploadCvButton} />
                </S.UploadButton>
              </Column>
            </Row>
          </>
        )}
        <input
          hidden
          multiple={maxFiles > 1}
          type="file"
          accept={DEFAULT_ACCEPTED_MIME_TYPES}
          name="fileToUpload"
          ref={uploadFileRef}
          onChange={handleUploadFile}
          data-testid={`upload-input-${trackingOrigin}`}
          data-cy={`documents-upload-input-${trackingOrigin}`}
        />
        {uploadError && (
          <S.ErrorBannerWrapper>
            <StatusBanner
              variant="error"
              display="inline"
              handleOnClose={handleErrorBannerClose}
              data-testid="upload-error-banner"
            >
              <FormattedMessage id={UPLOAD_ERRORS[uploadError]} values={{ maxFiles }} />
            </StatusBanner>
          </S.ErrorBannerWrapper>
        )}
        {uploads.length > 0 && (
          <S.UploadsList $isReduced={isReduced} uploads={uploads} onDeleteUpload={handleDeleteUpload} />
        )}
      </>
    );
  }
);
